import { useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import {
  CompoundPrismCalc,
  DecentrationPrismCalc,
  LensThicknessCalc,
  MinDiameterCalc,
  NearIntermediateCalc,
  ObliqueMeridianCalc,
  SphericalEquivalentCalc,
  TranspositionCalc,
  VertexCalc,
} from './cards/basic';
import { BlankSizeCalc, TiltWrapCalc, VerticalImbalanceCalc } from './cards/lab';
import { RxValidatorCalc, SellUpCalc, WeightMaterialCalc } from './cards/sales';
import { BlockHeader, CalcCard } from './ui/primitives';
import styles from './CalculatorsSection.module.css';

// Секция калькуляторов на главной. Структура — из прототипа партнёра: три
// блока-аккордеона, внутри — карточки, тоже аккордеоны; открыт всегда один
// блок и один калькулятор (повторный клик закрывает). Доступ без регистрации.
// У длинного блока хвост свёрнут: видны первые VISIBLE_LIMIT карточек +
// строка «Показать больше N», чтобы блок не закрывал контент под секцией.
//
// Формулы живут отдельно (`./formulas`, чистые функции + юнит-тесты), карточки
// — в `./cards`, примитивы — в `./ui/primitives`.

type BlockId = 'basic' | 'lab' | 'sales';

interface CalculatorEntry {
  /** Ключ i18n калькулятора (`calculators.<id>.title`). */
  id: string;
  Component: ComponentType;
}

const BLOCKS: { id: BlockId; items: CalculatorEntry[] }[] = [
  {
    id: 'basic',
    items: [
      { id: 'transposition', Component: TranspositionCalc },
      { id: 'minDiameter', Component: MinDiameterCalc },
      { id: 'thickness', Component: LensThicknessCalc },
      { id: 'decentration', Component: DecentrationPrismCalc },
      { id: 'vertex', Component: VertexCalc },
      { id: 'sphericalEquivalent', Component: SphericalEquivalentCalc },
      { id: 'oblique', Component: ObliqueMeridianCalc },
      { id: 'near', Component: NearIntermediateCalc },
      { id: 'compoundPrism', Component: CompoundPrismCalc },
    ],
  },
  {
    id: 'lab',
    items: [
      { id: 'blankSize', Component: BlankSizeCalc },
      { id: 'tiltWrap', Component: TiltWrapCalc },
      { id: 'imbalance', Component: VerticalImbalanceCalc },
    ],
  },
  {
    id: 'sales',
    items: [
      { id: 'weight', Component: WeightMaterialCalc },
      { id: 'sellUp', Component: SellUpCalc },
      { id: 'rx', Component: RxValidatorCalc },
    ],
  },
];

// Сколько карточек длинного блока видно до «Показать больше».
const VISIBLE_LIMIT = 3;

export function CalculatorsSection(): JSX.Element {
  const { t } = useTranslation();
  const [openBlock, setOpenBlock] = useState<BlockId | null>('basic');
  const [openCalc, setOpenCalc] = useState<string | null>(null);
  // Раскрыт ли хвост открытого блока (сбрасывается при смене блока).
  const [showAll, setShowAll] = useState(false);

  return (
    <section className={styles.section} aria-labelledby="calculators-title">
      <h2 className={styles.title} id="calculators-title">
        {t('calculators.title')}
      </h2>

      {/* Дисклеймер тестового режима. Держим до вычитки формул специалистом:
          в прототипе партнёра нашлись ошибки в оптических формулах (D1–D6,
          накопитель followups-v1 №7), поэтому расчётам нельзя доверять
          вслепую, пока их не подтвердит носитель предметной области. */}
      <p className={styles.betaNotice} role="note">
        {t('calculators.betaNotice')}
      </p>

      {BLOCKS.map((block) => (
        <div key={block.id}>
          <BlockHeader
            label={t(`calculators.blocks.${block.id}`)}
            count={block.items.length}
            open={openBlock === block.id}
            onToggle={() => {
              setOpenBlock(openBlock === block.id ? null : block.id);
              setShowAll(false);
            }}
          />
          {openBlock === block.id && (
            <div className={styles.blockBody}>
              {(showAll ? block.items : block.items.slice(0, VISIBLE_LIMIT)).map(
                ({ id, Component }) => (
                  <CalcCard
                    key={id}
                    label={t(`calculators.${id}.title`)}
                    open={openCalc === id}
                    onToggle={() => setOpenCalc(openCalc === id ? null : id)}
                  >
                    <Component />
                  </CalcCard>
                ),
              )}
              {block.items.length > VISIBLE_LIMIT && (
                <button
                  type="button"
                  className={styles.moreRow}
                  aria-expanded={showAll}
                  onClick={() => {
                    if (!showAll) {
                      setShowAll(true);
                      return;
                    }
                    setShowAll(false);
                    // Открытый калькулятор из хвоста закрываем вместе с хвостом.
                    if (block.items.slice(VISIBLE_LIMIT).some((item) => item.id === openCalc)) {
                      setOpenCalc(null);
                    }
                  }}
                >
                  <span>{showAll ? t('calculators.showLess') : t('calculators.showMore')}</span>
                  <span className={styles.moreMeta}>
                    {!showAll && (
                      <span className={styles.moreCount}>{block.items.length - VISIBLE_LIMIT}</span>
                    )}
                    <ChevronDown
                      size={15}
                      aria-hidden="true"
                      className={`${styles.moreChevron} ${showAll ? styles.moreChevronOpen : ''}`}
                    />
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
